import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

export default function VisualizeSimulation() {
  const [matList, setMatList] = useState([]);
  const [selectedMat, setSelectedMat] = useState('');
  const [matShape, setMatShape] = useState(null);     // [N, T, H, W]
  const [selectedIndices, setSelectedIndices] = useState([0, 1]);

  // 每个 slot 的状态：url / loading / error
  const [videoUrls, setVideoUrls] = useState({});     // {slot: url}
  const [loading, setLoading] = useState({});         // {slot: boolean}
  const [errors, setErrors] = useState({});           // {slot: string|null}

  // 为了抑制“旧请求回写”，对每个 slot 维护独立 runId
  const slotRunIdRef = useRef({ 0: 0, 1: 0 });
  // 持有每个 slot 的 AbortController，方便取消上一次该 slot 的请求
  const controllersRef = useRef({});

  // 生成 cache-busting 的 url（避免缓存住旧 404）
  const bust = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}ts=${Date.now()}` : url);

  // ========================= 取 .mat 列表 =========================
  useEffect(() => {
    axios.get('/api/mat-data/', { params: { action: 'list' } })
      .then(res => {
        setMatList(res.data);
        if (res.data.length > 0) setSelectedMat(res.data[0]);
      })
      .catch(err => {
        console.error('❌ Failed to fetch .mat list:', err);
      });
  }, []);

  // ========================= 取 shape（不主动触发生成） =========================
  useEffect(() => {
    if (!selectedMat) return;

    const controller = new AbortController();
    axios.get('/api/mat-data/', {
      params: { action: 'shape', filename: selectedMat },
      signal: controller.signal,
    })
      .then(res => {
        setMatShape(res.data.shape);        // [N, T, H, W]
        setSelectedIndices([0, 1]);         // 重置索引
        setVideoUrls({});                   // 清空旧视频
        setLoading({});                     // 清空 loading
        setErrors({});                      // 清空错误
        // 取消任何正在跑的 slot 请求
        Object.values(controllersRef.current || {}).forEach(c => c?.abort?.());
        controllersRef.current = {};
        slotRunIdRef.current = { 0: 0, 1: 0 };
      })
      .catch(err => {
        if (axios.isCancel(err)) return;
        console.error('❌ Failed to fetch mat shape:', err);
        setMatShape(null);
      });

    return () => controller.abort();
  }, [selectedMat]);

  // 当前文件名的 base（用于标题）
  const baseName = useMemo(
    () => (selectedMat ? selectedMat.replace(/\.mat$/i, '') : ''),
    [selectedMat]
  );

  // ========================= 单 slot 触发生成 =========================
  const clamp = (v, nMax) => Math.max(0, Math.min(Number(v) || 0, Math.max(0, (nMax ?? 1) - 1)));

  const requestVideoForSlot = (slot) => {
    if (!selectedMat || !matShape) return;
    const idx = clamp(selectedIndices[slot], matShape?.[0]);

    // 取消该 slot 之前的请求
    try { controllersRef.current[slot]?.abort?.(); } catch {}
    const controller = new AbortController();
    controllersRef.current[slot] = controller;

    // 递增该 slot 的 runId
    slotRunIdRef.current[slot] = (slotRunIdRef.current[slot] || 0) + 1;
    const runId = slotRunIdRef.current[slot];

    // 设置该 slot 的 loading
    setLoading(prev => ({ ...prev, [slot]: true }));
    setErrors(prev => ({ ...prev, [slot]: null }));

    axios.get('/api/mat-data/', {
      params: { action: 'video_url', filename: selectedMat, index: idx },
      signal: controller.signal,
    })
      .then(res => {
        // 只允许最新一轮写回
        if (slotRunIdRef.current[slot] !== runId) return;
        const url = res?.data?.video_url || null;
        setVideoUrls(prev => ({ ...prev, [slot]: url ? bust(url) : null }));
        setLoading(prev => ({ ...prev, [slot]: false }));
        setErrors(prev => ({ ...prev, [slot]: null }));
      })
      .catch(err => {
        if (axios.isCancel(err)) return;
        if (slotRunIdRef.current[slot] !== runId) return;
        setLoading(prev => ({ ...prev, [slot]: false }));
        setErrors(prev => ({
          ...prev,
          [slot]: (err?.response?.data?.error || err.message || 'Unknown error'),
        }));
      });
  };

  // ========================= 事件：只触发当前 slot =========================
  const handleIndexChange = (slot, value) => {
    const v = clamp(value, matShape?.[0]);
    setSelectedIndices(prev => {
      const cp = [...prev];
      cp[slot] = v;
      return cp;
    });
    // 只生成当前 slot
    requestVideoForSlot(slot);
  };

  // ========================= UI（保持不变） =========================
  return (
    <div className="h-full flex items-center justify-center px-4 py-6 bg-gray-100 text-black">
      <div className="w-full max-w-4xl bg-white shadow-md rounded-lg p-4">
        <h1 className="text-3xl font-bold mb-6 text-center text-blue-600">
          📁 Simulation Data Visualization
        </h1>

        {/* 文件选择 & shape */}
        <div className="flex flex-col md:flex-row gap-4 mb-6 text-sm">
          <div className="flex-1">
            <label className="block font-semibold mb-2">Select .mat File:</label>
            <select
              className="w-full p-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={selectedMat}
              onChange={(e) => setSelectedMat(e.target.value)}
            >
              {matList.map((file, idx) => (
                <option key={idx} value={file}>{file}</option>
              ))}
            </select>
          </div>

          {matShape && (
            <div className="flex-1">
              <label className="block font-semibold mb-2">Shape (N, T, H, W):</label>
              <p className="p-2 border border-gray-300 rounded bg-gray-50">
                [{matShape.join(', ')}]
              </p>
            </div>
          )}
        </div>

        {/* 帧选择（保持文案与样式不变） */}
        {matShape && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {[0, 1].map((slot) => (
              <div key={slot} className="flex flex-col">
                <label className="block font-semibold mb-2">
                  Select Track Index for Slot #{slot + 1}:
                </label>
                <select
                  className="p-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={selectedIndices[slot]}
                  onChange={(e) => handleIndexChange(slot, e.target.value)}
                >
                  {Array.from({ length: matShape[0] }, (_, i) => (
                    <option key={i} value={i}>Track #{i}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* 视频区域：逐卡片独立显示 */}
        {matShape && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[0, 1].map((slot) => {
              const url = videoUrls[slot];
              const isLoading = !!loading[slot];
              const err = errors[slot];

              return (
                <div key={slot} className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                  <h2 className="text-sm text-center font-medium text-blue-600 mb-2">
                    🎞️ {baseName ? `${baseName} — ` : ''}Track #{selectedIndices[slot]}
                  </h2>

                  {isLoading && (
                    <div className="flex items-center justify-center h-40 text-gray-500">
                      <div className="animate-pulse">Generating video…</div>
                    </div>
                  )}

                  {!isLoading && err && (
                    <div className="flex flex-col items-center justify-center h-40 text-red-600">
                      <div className="mb-2">Failed: {String(err)}</div>
                      <button
                        className="px-3 py-1 text-sm rounded bg-red-100 hover:bg-red-200"
                        onClick={() => requestVideoForSlot(slot)}   // 直接重试当前 slot
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {!isLoading && !err && url && (
                    <video
                      key={url}         // url 变化强制刷新
                      src={url}
                      controls
                      className="w-full rounded shadow"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
